import { Form, Input, InputNumber, message, Modal, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { accountStore } from 'store/accountStore';
import {
  PRESET_ASPECT_RATIO_OPTIONS,
  PRESET_DEFAULT_REFRAME_MODE,
  PRESET_LANGUAGE_OPTIONS,
  PRESET_REFRAME_MODE_OPTIONS,
  PRESET_TYPE_HINTS,
  PRESET_TYPE_OPTIONS,
} from '../constants';
import {
  extractPresetFieldErrors,
  formatPresetErrorMessage,
  shortsPresetService,
} from '../services/shortsPresetService';
import { PresetType, ShortsPresetInterface, ShortsPresetRequest } from '../types/shortsPresetType';

interface ShortsPresetFormModalProps {
  open: boolean;
  /** null → create mode, otherwise edit mode. */
  preset: ShortsPresetInterface | null;
  onClose: () => void;
  onSaved: () => void;
}

interface PresetFormValues {
  name: string;
  type: ShortsPresetRequest['type'];
  count: number;
  aspect_ratio: ShortsPresetRequest['aspect_ratio'];
  min_duration: number;
  max_duration: number;
  reframe_mode: ShortsPresetRequest['reframe_mode'];
  subtitle?: ShortsPresetRequest['subtitle'];
  dub?: ShortsPresetRequest['dub'];
  description?: string;
}

/** Form fields that can carry a server-side field error. */
const FORM_FIELD_NAMES: (keyof PresetFormValues)[] = [
  'name',
  'type',
  'count',
  'aspect_ratio',
  'min_duration',
  'max_duration',
  'reframe_mode',
  'subtitle',
  'dub',
  'description',
];

const isFormFieldName = (field: string): field is keyof PresetFormValues =>
  (FORM_FIELD_NAMES as string[]).includes(field);

const ShortsPresetFormModal: React.FC<ShortsPresetFormModalProps> = ({
  open,
  preset,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<PresetFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState<PresetType | undefined>();
  const isEdit = preset !== null;

  useEffect(() => {
    if (!open) {
      return;
    }
    if (preset) {
      form.setFieldsValue({
        name: preset.name,
        type: preset.type,
        count: preset.count,
        aspect_ratio: preset.aspect_ratio,
        min_duration: preset.min_duration,
        max_duration: preset.max_duration,
        reframe_mode: preset.reframe_mode ?? PRESET_DEFAULT_REFRAME_MODE,
        subtitle: preset.subtitle ?? undefined,
        dub: preset.dub ?? undefined,
        description: preset.description ?? '',
      });
      setSelectedType(preset.type);
    } else {
      form.resetFields();
      form.setFieldsValue({ reframe_mode: PRESET_DEFAULT_REFRAME_MODE });
      setSelectedType(undefined);
    }
  }, [open, preset, form]);

  /**
   * Map `errors.<field>` onto the matching form fields; anything without a field
   * (e.g. platform_id, which has no input) falls back to a toast.
   */
  const applyServerErrors = (error: unknown, fallback: string): void => {
    const fieldErrors = extractPresetFieldErrors(error);
    const entries = Object.entries(fieldErrors);
    const matched = entries.filter(([field]) => isFormFieldName(field));

    if (matched.length > 0) {
      form.setFields(
        matched.map(([name, errors]) => ({ name: name as keyof PresetFormValues, errors }))
      );
    }

    const unmatched = entries.filter(([field]) => !isFormFieldName(field));
    if (matched.length === 0 || unmatched.length > 0) {
      message.error(formatPresetErrorMessage(error, fallback));
    }
  };

  const handleSubmit = async () => {
    let values: PresetFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload: ShortsPresetRequest = {
      name: values.name.trim(),
      type: values.type,
      count: values.count,
      aspect_ratio: values.aspect_ratio,
      min_duration: values.min_duration,
      max_duration: values.max_duration,
      reframe_mode: values.reframe_mode ?? PRESET_DEFAULT_REFRAME_MODE,
      // Optional language fields cleared in the UI must be explicit nulls, not omitted.
      subtitle: values.subtitle ?? null,
      dub: values.dub ?? null,
      description: values.description ?? '',
    };

    setSubmitting(true);
    try {
      if (isEdit && preset) {
        await shortsPresetService.updatePreset(preset.id, payload);
        message.success('Preset updated');
      } else {
        // platform_id is write-only and required on create only.
        const platformId = accountStore.getAccount()?.platform?.id;
        if (!platformId) {
          message.error('Cannot determine the current platform. Please reload and try again.');
          return;
        }
        await shortsPresetService.createPreset({ ...payload, platform_id: platformId });
        message.success('Preset created');
      }
      onSaved();
    } catch (error) {
      applyServerErrors(error, isEdit ? 'Failed to update preset' : 'Failed to create preset');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit Shorts Preset' : 'New Shorts Preset'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Save"
      confirmLoading={submitting}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter a preset name' },
            { max: 255, message: 'Name must be 255 characters or fewer' },
          ]}
        >
          <Input placeholder="e.g. Variety vertical highlights" />
        </Form.Item>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            label="Type"
            name="type"
            style={{ flex: 1 }}
            extra={selectedType ? PRESET_TYPE_HINTS[selectedType] : undefined}
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              placeholder="Select type"
              options={PRESET_TYPE_OPTIONS}
              onChange={setSelectedType}
            />
          </Form.Item>
          <Form.Item
            label="Count (per episode)"
            name="count"
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Please enter a count' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} precision={0} />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            label="Aspect Ratio"
            name="aspect_ratio"
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Please select an aspect ratio' }]}
          >
            <Select placeholder="Select ratio" options={PRESET_ASPECT_RATIO_OPTIONS} />
          </Form.Item>
          <Form.Item label="Reframe Mode" name="reframe_mode" style={{ flex: 1 }}>
            <Select options={PRESET_REFRAME_MODE_OPTIONS} />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            label="Min Duration (seconds)"
            name="min_duration"
            style={{ flex: 1 }}
            rules={[
              { required: true, message: 'Please enter a minimum duration' },
              // Mirrors the backend cross-field check so it surfaces before saving.
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const max = getFieldValue('max_duration');
                  // Only compare once both ends are filled in; `required` covers the rest.
                  const bothSet =
                    value !== null && value !== undefined && max !== null && max !== undefined;
                  if (!bothSet || value <= max) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Min duration must not exceed max duration'));
                },
              }),
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              onChange={() => form.validateFields(['min_duration'])}
            />
          </Form.Item>
          <Form.Item
            label="Max Duration (seconds)"
            name="max_duration"
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Please enter a maximum duration' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              // Re-run the paired rule that lives on min_duration.
              onChange={() => form.validateFields(['min_duration'])}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item label="Subtitle" name="subtitle" style={{ flex: 1 }}>
            <Select allowClear placeholder="None" options={PRESET_LANGUAGE_OPTIONS} />
          </Form.Item>
          <Form.Item label="Dub" name="dub" style={{ flex: 1 }}>
            <Select allowClear placeholder="None" options={PRESET_LANGUAGE_OPTIONS} />
          </Form.Item>
        </div>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ShortsPresetFormModal;
